import React, { useState } from 'react';
import {
    styled,
    Box,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    Button,
    //
} from '@mui/material';
import { Close, DragHandle, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// workaround: unable to use transform as it clashes with draggable elements
const ParentContainer = styled(Box)(() => ({
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const ModalWrap = styled(Box)(() => ({
    backgroundColor: '#FFFFFF',
    borderRadius: 5,
    padding: 24,
    boxShadow: 6,
    width: 470,
    overflow: 'auto',
}));

const DraggableWrap = styled(Box)(() => ({
    backgroundColor: '#FAFAFA',
    borderRadius: 5,
    maxHeight: 470,
    overflow: 'scroll',
}));

const DraggableListItem = styled(ListItem)(() => ({
    backgroundColor: '#FFFFFF',
    borderRadius: 5,
    display: 'flex',
    alignItems: 'center',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    margin: 6,
    marginTop: 0,
}));

const PriorityModal = ({ label, options, setOpenModal, setPriorityList }) => {
    const [newSelection, setNewSelection] = useState(options);

    const handleCancel = () => {
        setOpenModal(false);
    };

    const handleApply = () => {
        setPriorityList(newSelection);
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(newSelection);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setNewSelection(items);
    };

    const moveItem = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= newSelection.length) return;

        const updatedSelection = [...newSelection];
        const itemToMove = updatedSelection[index];
        updatedSelection.splice(index, 1);
        updatedSelection.splice(newIndex, 0, itemToMove);

        setNewSelection(updatedSelection);
    };

    return (
        <ParentContainer>
            <ModalWrap>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                    //
                >
                    <Typography variant="h5">{label}</Typography>
                    <IconButton onClick={() => setOpenModal(false)}>
                        <Close />
                    </IconButton>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography
                        variant="body1"
                        fontWeight={600}
                        component="div"
                        sx={{ flex: 1 }}
                        //
                    >
                        Order
                    </Typography>
                    <Typography
                        variant="body1"
                        fontWeight={600}
                        component="div"
                        sx={{ flex: 4 }}
                        //
                    >
                        Plants
                    </Typography>
                </Box>
                <DraggableWrap>
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="droppable-list">
                            {(droppable) => (
                                <List {...droppable.droppableProps} ref={droppable.innerRef}>
                                    {newSelection.map((option, index) => (
                                        <Draggable key={option._id} draggableId={option._id} index={index}>
                                            {(draggable) => (
                                                <Box display="flex" justifyContent="center" alignItems="center">
                                                    <DraggableListItem
                                                        ref={draggable.innerRef}
                                                        {...draggable.draggableProps}
                                                        {...draggable.dragHandleProps}
                                                        //
                                                    >
                                                        <Typography variant="body1" component="div" sx={{ flex: 1 }}>
                                                            {index + 1}
                                                        </Typography>
                                                        <ListItemIcon>
                                                            <DragHandle />
                                                        </ListItemIcon>
                                                        <ListItemText primary={option.name} sx={{ flex: 4 }} />
                                                        <IconButton disabled={index === 0} onClick={() => moveItem(index, -1)}>
                                                            <ArrowUpward />
                                                        </IconButton>
                                                        <IconButton disabled={index === options.length - 1} onClick={() => moveItem(index, 1)}>
                                                            <ArrowDownward />
                                                        </IconButton>
                                                    </DraggableListItem>
                                                </Box>
                                            )}
                                        </Draggable>
                                    ))}
                                    {droppable.placeholder}
                                </List>
                            )}
                        </Droppable>
                    </DragDropContext>
                </DraggableWrap>
                <Box display="flex" justifyContent="end" marginTop={2}>
                    <Button
                        onClick={handleCancel}
                        variant="outlined"
                        color="secondary"
                        //
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleApply}
                        variant="contained"
                        color="primary"
                        sx={{ ml: 2 }}
                        //
                    >
                        Apply
                    </Button>
                </Box>
            </ModalWrap>
        </ParentContainer>
    );
};

export default PriorityModal;
